import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  Upload,
  Loader2,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  Receipts,
  fileToBase64,
  RECEIPT_CATEGORIES,
  PAYMENT_METHODS,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const today = () => new Date().toISOString().slice(0, 10);

const empty = {
  vendor: "",
  receipt_date: today(),
  total_amount: "",
  gst_amount: "",
  payment_method: "",
  category: "Other",
  notes: "",
};

const Field = ({ label, children, testid }) => (
  <label data-testid={testid} className="block">
    <div className="swiss-label mb-1">{label}</div>
    {children}
  </label>
);

const CaptureReceipt = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [form, setForm] = useState(empty);
  const [imageBase64, setImageBase64] = useState(null);
  const [imageMime, setImageMime] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ocring, setOcring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = async (file) => {
    if (!file) return;
    const isImage = /^image\//.test(file.type);
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Please use an image (JPG/PNG/WEBP) or PDF.");
      return;
    }
    try {
      const { base64, mime } = await fileToBase64(file);
      setImageBase64(base64);
      setImageMime(mime);
      setImagePreview(isImage ? `data:${mime};base64,${base64}` : null);
      await runOCR(base64, mime);
    } catch {
      toast.error("Could not read the file.");
    }
  };

  const runOCR = async (base64, mime) => {
    setOcring(true);
    try {
      const res = await Receipts.ocr({ image_base64: base64, image_mime: mime });
      setForm((prev) => {
        const next = { ...prev };
        let filled = 0;
        if (res.vendor) {
          next.vendor = res.vendor;
          filled++;
        }
        if (res.receipt_date) {
          next.receipt_date = res.receipt_date;
          filled++;
        }
        if (res.total_amount != null) {
          next.total_amount = String(res.total_amount);
          filled++;
        }
        if (res.gst_amount != null) {
          next.gst_amount = String(res.gst_amount);
          filled++;
        }
        if (res.payment_method) {
          next.payment_method = res.payment_method;
          filled++;
        }
        if (res.category) {
          next.category = res.category;
          filled++;
        }
        if (res.notes) {
          next.notes = res.notes;
          filled++;
        }
        setTimeout(() => {
          toast.success(
            filled > 0
              ? `AI pre-filled ${filled} field${filled === 1 ? "" : "s"}. Review before saving.`
              : "AI couldn't confidently read the receipt. Please enter manually.",
          );
        }, 0);
        return next;
      });
    } catch (e) {
      toast.error("OCR failed. You can still enter the details manually.");
    } finally {
      setOcring(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const onSave = async (e) => {
    e?.preventDefault();
    if (!form.vendor.trim()) return toast.error("Vendor / shop name is required");
    const amount = Number(form.total_amount);
    if (!amount || amount <= 0)
      return toast.error("Total amount must be greater than zero");
    if (!form.receipt_date) return toast.error("Receipt date is required");

    setSaving(true);
    try {
      const payload = {
        vendor: form.vendor.trim(),
        receipt_date: form.receipt_date,
        total_amount: amount,
        gst_amount: Number(form.gst_amount) || 0,
        payment_method: form.payment_method || "",
        category: form.category || "Other",
        notes: form.notes || "",
        image_base64: imageBase64,
        image_mime: imageMime,
      };
      const created = await Receipts.create(payload);
      toast.success("Receipt saved.");
      navigate(`/receipts/${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="capture-receipt" className="fade-rise">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="swiss-label mb-3">Capture</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        New Receipt
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Snap or upload a receipt — Billy reads the vendor, total, GST, payment
        method and date, and you save it to the cloud.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 mt-10">
        {/* Capture side */}
        <div>
          <div
            data-testid="dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`dropzone ${dragging ? "dragging" : ""}`}
          >
            {imagePreview ? (
              <div className="flex flex-col items-center gap-4">
                <img
                  data-testid="receipt-preview"
                  src={imagePreview}
                  alt="Receipt preview"
                  className="max-h-[420px] w-auto border border-neutral-200"
                />
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    data-testid="reupload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-black hover:text-white transition-colors"
                  >
                    Replace Image
                  </button>
                  <button
                    data-testid="rerun-ocr-btn"
                    disabled={ocring}
                    onClick={() => runOCR(imageBase64, imageMime)}
                    className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 disabled:opacity-50 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                  >
                    {ocring ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Re-run AI Read
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-6">
                <div className="h-16 w-16 border-2 border-black flex items-center justify-center">
                  <Camera className="h-7 w-7" strokeWidth={2} />
                </div>
                <div className="text-center">
                  <div className="font-display font-extrabold text-2xl">
                    Snap or drop a receipt
                  </div>
                  <div className="text-neutral-500 text-sm mt-1">
                    JPG, PNG, or WEBP. AI extracts the details.
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="take-photo-btn"
                    onClick={() => cameraInputRef.current?.click()}
                    className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                  >
                    <Camera className="h-4 w-4" /> Take Photo
                  </button>
                  <button
                    data-testid="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" /> Upload Receipt
                  </button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <input
              ref={cameraInputRef}
              data-testid="camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {ocring && (
            <div
              data-testid="ocr-loading"
              className="mt-4 inline-flex items-center gap-2 text-sm text-neutral-600"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> AI is reading your
              receipt…
            </div>
          )}
        </div>

        {/* Form side */}
        <form
          data-testid="receipt-form"
          onSubmit={onSave}
          className="space-y-5"
        >
          <Field label="Vendor / Shop" testid="field-vendor">
            <Input
              data-testid="input-vendor"
              className="swiss-input"
              value={form.vendor}
              onChange={update("vendor")}
              placeholder="Bunnings, BP, Officeworks…"
            />
          </Field>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Receipt Date" testid="field-receipt-date">
              <Input
                data-testid="input-receipt-date"
                type="date"
                className="swiss-input"
                value={form.receipt_date}
                onChange={update("receipt_date")}
              />
            </Field>
            <Field label="Total (incl. GST)" testid="field-total">
              <Input
                data-testid="input-total"
                className="swiss-input mono"
                value={form.total_amount}
                onChange={update("total_amount")}
                placeholder="0.00"
                inputMode="decimal"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Field label="GST" testid="field-gst">
              <Input
                data-testid="input-gst"
                className="swiss-input mono"
                value={form.gst_amount}
                onChange={update("gst_amount")}
                placeholder="0.00"
                inputMode="decimal"
              />
            </Field>
            <Field label="Payment Method" testid="field-payment-method">
              <select
                data-testid="input-payment-method"
                className="swiss-input w-full appearance-none cursor-pointer"
                value={form.payment_method}
                onChange={update("payment_method")}
              >
                <option value="">— Select —</option>
                {PAYMENT_METHODS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Category" testid="field-category">
            <div className="flex flex-wrap gap-2">
              {RECEIPT_CATEGORIES.map((c) => {
                const active = form.category === c;
                return (
                  <button
                    type="button"
                    key={c}
                    data-testid={`category-${c}`}
                    onClick={() => setForm((f) => ({ ...f, category: c }))}
                    className={`px-3 py-2 border-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                      active
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Notes" testid="field-notes">
            <Textarea
              data-testid="input-notes"
              className="swiss-input resize-none"
              value={form.notes}
              onChange={update("notes")}
              rows={3}
              placeholder="What was bought?"
            />
          </Field>

          <div className="pt-4 flex flex-wrap gap-3 items-center">
            <button
              data-testid="save-receipt-btn"
              type="submit"
              disabled={saving || ocring}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Receipt
            </button>
            <button
              data-testid="cancel-btn"
              type="button"
              onClick={() => navigate("/receipts")}
              className="text-xs uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaptureReceipt;
